import { Image, PanelType, StackItem } from '@fluentui/react';
import Compressor from 'compressorjs';
import { DefaultButton, IconButton, PrimaryButton, Stack } from 'office-ui-fabric-react';
import React, { Component } from 'react';
import Scriba from 'react-scriba';
import ImmocomContext from '../../../contexts/ImmocomContext';
import PerimetreContext from '../../../contexts/PerimetreContext';
import { DataActualite } from '../../../data/DataActualite';
import { MaxLengths } from '../../../data/MaxLenghts';
import { Immocom } from '../../../specifique/immocom';
import { ComboBoxOperation } from '../../ComboBox/ComboBoxOperation.jsx';
import { EUIDetailsList } from '../../EUI/EUIDetailsList';
import { EUIDialog } from '../../EUI/EUIDialog';
import { EUIPanel } from '../../EUI/EUIPanel';
import { EUIRichTextField } from '../../EUI/EUIRichTextField';
import { EUITextField } from '../../EUI/EUITextField.jsx';
import { EUIToggle } from '../../EUI/EUIToggle';
import Actualite from './Actualite';
import Editor from './Editor';

const ACTIONS = {
    ENREGISTRER: 1,
    PUBLIER: 2,
    PREVISUALISER: 3,
};

// Composant intermédiaire car impossible dans les CC de consommer deux contexts
// Sera corrigé lorsque l'on migrara les CC en FC
export class FicheActualite extends Component {

    static contextType = PerimetreContext;

    static defaultProps = {
        idActualite: undefined,
        isOpen: false,
        peutEditerActualiteNationale: false,
        /* --- */
        onDismiss: () => null,
        onSave: () => null
    };

    render = () => {

        const { perimetre } = this.context;
        const { idActualite, isOpen, onDismiss, onSave, peutEditerActualiteNationale } = this.props;

        return <FicheActualiteWithPerimetre
            idActualite={idActualite}
            isOpen={isOpen}
            onDismiss={onDismiss}
            onSave={onSave}
            perimetre={perimetre}
            peutEditerActualiteNationale={peutEditerActualiteNationale}
        />;

    };

};

// KP - 19/09/2023 : composant raccordé à ImmocomContext pour tests
class FicheActualiteWithPerimetre extends Component {

    static contextType = ImmocomContext;

    static defaultProps = {
        idActualite: undefined,
        isOpen: false,
        perimetre: {},
        peutEditerActualiteNationale: false,
        /* --- */
        onDismiss: () => null,
        onSave: () => null
    };

    static defaultState = {
        actualite: {
            idActualite: 0,
            idOperation: undefined,
            nomCommercial: undefined, // Utilisé uniquement pour la prévisualisation
            nationale: false,
            titre: '',
            description: '',
            datePublication: null,
            creerPar: '',
            modifierPar: '',
        },
        afficherDialogSuppressionImage: false,
        afficherModalModificationImage: false,
        imageActive: {},
        images: [],
        onActiveBoutonEnregistrer: false,
        peutEditerActualite: true
    };

    constructor(props) {
        super(props);
        this.state = { ...FicheActualiteWithPerimetre.defaultState };
        this._columns = this._getColumns();
        this._fileUploadRef = React.createRef();
    };

    _getColumns = () => {

        const { peutEditerActualite } = this.state;

        return [{
            key: "src",
            name: "Aperçu",
            onRender: (item) => {
                if (!!item.key)
                    return <Image height={50} key={item.key} src={item.src} />
            }
        }, {
            key: "fileSizeInMb",
            name: "Poids (Mo)",
            onRender: item => {
                const { compressedFileSizeInMb, fileSizeInMb } = item;
                if (!compressedFileSizeInMb) return `${fileSizeInMb.toFixed(2)} Mo`;
                else return <Stack>
                    <strike>{`${fileSizeInMb.toFixed(2)} Mo`}</strike>
                    <span>{`${compressedFileSizeInMb.toFixed(2)} Mo`}</span>
                </Stack>;
            }
        }, {
            ariaLabel: "Télécharger l'image",
            isIconOnly: true,
            key: "download",
            onRender: (item) => {
                const { fileData, fileName } = item;
                return <IconButton
                    ariaLabel="Télécharger l'image"
                    iconProps={{ iconName: "Download" }}
                    onClick={() => {
                        const document = Scriba.fichier().Base64VersByteArray(fileData);
                        Scriba.fichier().EnregisterSous(document, fileName);
                    }}
                    title="Télécharger l'image"
                />;
            }
        }, {
            ariaLabel: "Compresser l'image",
            isIconOnly: true,
            key: "compress",
            onRender: (item) => {
                const { file, quality } = item;
                if (!file) return null;
                else return <IconButton
                    ariaLabel="Compresser l'image"
                    disabled={!peutEditerActualite || quality <= 0.6}
                    iconProps={{ iconName: "ImagePixel" }}
                    onClick={this._compressImage(item)}
                    title="Compresser l'image"
                />;
            }
        }, {
            ariaLabel: "Modifier l'image",
            isIconOnly: true,
            key: "edit",
            onRender: (item) => {
                return <IconButton
                    ariaLabel="Modifier l'image"
                    disabled={!peutEditerActualite}
                    iconProps={{ iconName: "Crop" }}
                    onClick={this._toggleModal("ModificationImage", true, item)}
                    title="Modifier l'image"
                />;
            }
        }, {
            ariaLabel: "Supprimer l'image",
            isIconOnly: true,
            key: "delete",
            onRender: (item) => {
                return <IconButton
                    ariaLabel="Supprimer l'image"
                    disabled={!peutEditerActualite}
                    iconProps={{ iconName: "Delete" }}
                    onClick={this._toggleDialog("SuppressionImage", true, item)}
                    title="Supprimer l'image"
                />;
            }
        }];

    };

    _getItems = () => {
        return [{
            disabled: !this.state.peutEditerActualite,
            key: "nouveau",
            name: "Nouvelle image",
            iconProps: { iconName: "Add" },
            onClick: () => !!this._fileUploadRef.current && this._fileUploadRef.current.click()
        }];
    };

    // La compression des images nécessite un objet de type File ou Blob
    // https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
    _b64toBlob = (b64Data, contentType = "image/png", sliceSize = 512) => {
        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    };

    _chargementDonnees = () => {

        const { addMessage, dismissMessage, fetchMessages } = this.context;
        const messageId = addMessage("loading", "Chargement de l'actualité en cours...");

        const { idActualite, peutEditerActualiteNationale } = this.props;

        DataActualite._chargementFicheActualite(idActualite).then(responseFetch => {
            const retourVerif = Immocom.onRegardeSiOnChargeLesDonnees(responseFetch, this);
            if (retourVerif === null) {

                const objetData = responseFetch.reponseAPIobjet;
                const actualite = objetData[0];
                const images = objetData[1];

                let peutEditerActualite = true;
                if (actualite.nationale)
                    peutEditerActualite = peutEditerActualiteNationale;

                const state = {
                    actualite: { ...FicheActualiteWithPerimetre.defaultState.actualite, ...actualite },
                    images: images.map((image, index) => {
                        const { fileData, nom } = image;

                        // https://softwareengineering.stackexchange.com/questions/288670/know-file-size-with-a-base64-string
                        let fileSizeInBytes = fileData.length * (3 / 4);
                        if (fileData.endsWith("==")) fileSizeInBytes = fileSizeInBytes - 2;
                        else if (fileData.endsWith("=")) fileSizeInBytes = fileSizeInBytes - 1;

                        return {
                            key: index + 1,
                            file: this._b64toBlob(fileData),
                            fileData: fileData,
                            fileName: nom,
                            fileSizeInMb: fileSizeInBytes / 1024 / 1024,
                            quality: 1,
                            src: `data:image/png;base64,${fileData}`
                        };
                    }),
                    peutEditerActualite
                };
                const callback = () => {
                    this._onVerificationFormulaire();
                    this._columns = this._getColumns();
                };
                Immocom.onReinitStateValeurParDefautEtExecutionDuSetStateEtExecutionCallBack(this, state, callback);
            }
            else fetchMessages(responseFetch);
            dismissMessage(messageId);
        });

    };

    _compressImage = (image) => () => {

        const { file, key, quality } = image;
        let newQuality = Math.max(0.6, (quality || 1) - 0.1);
        newQuality = Math.trunc(newQuality * 10) / 10;

        new Compressor(file, {
            quality: newQuality,
            success: result => {

                const reader = new FileReader();
                reader.readAsDataURL(result);
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    const fileData = dataUrl.split(",")[1];

                    this.setState((prevState) => {
                        let images = [...prevState.images];
                        const index = images.findIndex(img => img.key === key);
                        if (index >= 0) {
                            const compressedImage = {
                                ...images[index],
                                compressedFileSizeInMb: result.size / 1024 / 1024,
                                file: result,
                                fileData,
                                quality: newQuality,
                                src: dataUrl
                            }
                            images[index] = compressedImage;
                            return { images };
                        }
                    });
                }

            }
        });

    };

    _onChangeToggle = (e, isChecked) => {
        let actualite = { ...this.state.actualite, nationale: isChecked };
        if (isChecked) {
            actualite.idOperation = undefined;
            actualite.nomCommercial = undefined;
        }
        this.setState({ actualite }, this._onVerificationFormulaire);
    };

    _onEnregistrement = () => {
        this._onAction(ACTIONS.ENREGISTRER);
    };

    _onOpen = () => {
        const { idActualite, perimetre } = this.props;
        const { idOperation } = perimetre;

        let state = { ...FicheActualiteWithPerimetre.defaultState };
        if (!!idOperation)
            state.actualite.idOperation = idOperation;
        this.setState(state);

        if (!!idActualite)
            this._chargementDonnees();
    };

    _onVerificationFormulaire = () => {
        const { idActualite } = this.props;
        const { actualite } = this.state;
        Immocom.fiche()._validationFormulaire(this, 'Actualite', actualite, idActualite > 0 ? 'Modification' : 'Création');
    };

    _publier = () => {
        this._onAction(ACTIONS.PUBLIER);
    };

    _onAction = (action) => {

        const { ENREGISTRER, PUBLIER } = ACTIONS;
        let message = "Chargement en cours...";
        if (action === ENREGISTRER) message = "Enregistrement de l'actualité en cours...";
        else if (action === PUBLIER) message = "Publication de l'actualité en cours...";

        const { addMessage, dismissMessage, fetchMessages } = this.context;
        const loadingMessageId = addMessage("loading", message);

        const { onSave } = this.props;
        const { actualite, images } = this.state;

        let promise;
        if (action === ENREGISTRER) promise = () => DataActualite._onEnregistrementActualite(actualite, images);
        if (action === PUBLIER) promise = () => DataActualite._onEnregistrementEtPublicationActualite(actualite, images);

        if (typeof promise === "function")
            promise().then(responseFetch => {
                const retourVerif = Immocom.onRegardeSiOnChargeLesDonnees(responseFetch, this);
                if (retourVerif === null)
                    Immocom.onReinitStateValeurParDefautEtExecutionDuSetStateEtExecutionCallBack(this, {}, onSave);
                else fetchMessages(responseFetch);
                dismissMessage(loadingMessageId);
            });
        else dismissMessage(loadingMessageId);

    };

    _onImageEdition = (data) => {

        this.setState({ afficherModalModificationImage: false });

        const { imageActive, images } = this.state;
        const index = images.findIndex(image => image.key === imageActive.key);
        if (index >= 0) {
            const newImage = {
                ...imageActive,
                file: this._b64toBlob(data),
                quality: 1
            };
            this._compressImage(newImage)();
        }

    };

    _onUpload = (event) => {
        const files = event.target.files;
        if (files.length > 0) {
            for (var i = 0; i < files.length; i++) {
                const reader = new FileReader();
                const file = files[i];
                console.log(`Traitement de l'image n°${i + 1}/${files.length} : ${file.name}`)
                const method = () => {
                    this.setState(prev => {
                        let images = [...prev.images];
                        const key = Math.max(...images.map(item => item.key), 0) + 1;
                        images.push({
                            key,
                            file,
                            fileData: Scriba.fichier().ByteArrayVersBase64(reader.result),
                            fileName: file.name,
                            fileSizeInMb: file.size / 1024 / 1024,
                            quality: 1,
                            src: window.URL.createObjectURL(file)
                        });
                        return { images };
                    });
                };
                reader.addEventListener("load", method)
                reader.readAsArrayBuffer(file);
            }
        }
        event.target.value = null;
    };

    _togglePanel = (nomPanel, isOpen, item, callback) => () => {
        this.setState(
            {
                [`afficherPanel${nomPanel}`]: isOpen,
                itemActif: item || {},
            },
            typeof callback === "function" ? callback() : null
        );
    };

    _toggleDialog = (nomDialog, isOpen, item, callback) => () => {
        this.setState(
            {
                [`afficherDialog${nomDialog}`]: isOpen,
                imageActive: item || {},
            },
            typeof callback === "function" ? callback() : null
        );
    };

    _toggleModal = (nomModal, isOpen, item, callback) => () => {
        this.setState(
            {
                [`afficherModal${nomModal}`]: isOpen,
                imageActive: item || {},
            },
            typeof callback === "function" ? callback() : null
        );
    };

    _supprimerImage = (key) => () => {
        this.setState((prev) => {
            let images = [...prev.images.filter(image => image.key !== key)]; // Suppression de l'image
            return { images };
        });
    };

    render = () => {

        const { idActualite, isOpen, onDismiss, perimetre, peutEditerActualiteNationale } = this.props;
        const { idRegion, idAgence, idProgramme } = perimetre;
        const { onActiveBoutonEnregistrer, actualite, imageActive, afficherDialogSuppressionImage, afficherModalModificationImage, images, peutEditerActualite } = this.state;
        const labelSave = !actualite.datePublication ? 'Enregistrer le brouillon' : 'Enregistrer';
        const disabledPublier = !!actualite.datePublication;

        return <EUIPanel
            customPrimaryButtons=
            {[
                <PrimaryButton key="publish" onClick={this._publier} disabled={!onActiveBoutonEnregistrer || disabledPublier || !peutEditerActualite}>Publier</PrimaryButton>
            ]}
            headerText={!!idActualite ? `Modification de l'actualité #${idActualite}` : "Nouvelle actualité"}
            isFormValid={onActiveBoutonEnregistrer && peutEditerActualite}
            isOpen={isOpen}
            onDismiss={onDismiss}
            onOpen={this._onOpen}
            onSave={this._onEnregistrement}
            type={PanelType.large}
            labelSave={labelSave}
        >

            <Stack horizontal tokens={{ childrenGap: 15 }}>

                <StackItem styles={{ root: { flex: "1 1 0px" } }}>

                    <Stack horizontal tokens={{ childrenGap: '8px' }}>
                        <EUIToggle
                            checked={actualite.nationale}
                            disabled={!peutEditerActualiteNationale}
                            label='Nationale'
                            onChange={this._onChangeToggle}
                        />
                        <StackItem grow>
                            <ComboBoxOperation
                                disabled={actualite.nationale || !peutEditerActualite}
                                filtreSurMonPerimetre
                                idAgence={idAgence}
                                idAgenceRequired={false}
                                idProgramme={idProgramme}
                                idRegion={idRegion}
                                onChange={(event, option, index, value, item) => Scriba.state().setObjetProprietes(this, "actualite", [["idOperation", option.key], ["nomCommercial", item.nomCommercial]], this._onVerificationFormulaire)}
                                onLoad={!!actualite.idOperation ? item => Scriba.state().setObjetPropriete(this, "actualite", "nomCommercial", item.nomCommercial) : null}
                                perimetre="eiffage"
                                required
                                selectedKey={actualite.idOperation}
                            />
                        </StackItem>
                    </Stack>

                    <EUITextField
                        disabled={!peutEditerActualite}
                        label='Titre'
                        maxLength={MaxLengths.actualite.titre}
                        onDelayedChange={(event, newValue) => Scriba.state().setObjetPropriete(this, "actualite", "titre", newValue, this._onVerificationFormulaire)}
                        required
                        value={actualite.titre}
                    />

                    <EUIRichTextField
                        disabled={!peutEditerActualite}
                        label="Description"
                        maxLength={MaxLengths.actualite.description}
                        onChange={() => this.setState({ onActiveBoutonEnregistrer: false })}
                        onDelayedChange={(event, newValue) => Scriba.state().setObjetPropriete(this, "actualite", "description", newValue, this._onVerificationFormulaire)}
                        required
                        value={actualite.description}
                    />

                    {(!!actualite.creerLe || !!actualite.modifierLe || !!actualite.datePublication)
                        ? <Stack styles={{ root: { marginTop: 8, marginBottom: 8 } }}>
                            {!!actualite.creerLe && <span>Créée par <b>{actualite.creerPar}</b> le <b>{Scriba.date().stringDateFormatCourt(actualite.creerLe)}</b></span>}
                            {!!actualite.modifierLe && <span>Modifiée par <b>{actualite.modifierPar}</b> le <b>{Scriba.date().stringDateFormatCourt(actualite.modifierLe)}</b></span>}
                            {!!actualite.datePublication && <span>Publiée le <b>{Scriba.date().stringDateFormatCourt(actualite.datePublication)}</b></span>}
                        </Stack>
                        : <br />
                    }

                    <input type="file" accept="image/png, image/jpeg" ref={this._fileUploadRef} multiple onChange={this._onUpload} style={{ display: "none" }} />

                    <EUIDetailsList
                        columns={this._columns}
                        getKey={item => item.key}
                        items={images}
                        menuItems={this._getItems()}
                        onDragAndDropped={peutEditerActualite ? (reorderedItems) => this.setState({ images: reorderedItems }) : undefined}
                        wrapInScrollablePane={false}
                    />

                </StackItem>

                <StackItem styles={{ root: { flex: "1 1 0px", marginTop: 15, maxWidth: 720 } }}>
                    <Actualite
                        datePublication={actualite.datePublication || Date.now()}
                        description={actualite.description}
                        images={images}
                        nomCommercial={actualite.nomCommercial}
                        titre={actualite.titre}
                    />
                </StackItem>

            </Stack>

            <EUIDialog
                buttons={[
                    <PrimaryButton key="delete" onClick={this._toggleDialog("SuppressionImage", false, {}, this._supprimerImage(imageActive.key))} text="Supprimer" />,
                    <DefaultButton key="cancel" onClick={this._toggleDialog("SuppressionImage", false)} text="Annuler" />
                ]}
                isOpen={afficherDialogSuppressionImage}
                onDismiss={this._toggleDialog("SuppressionImage", false)}
                titre="Suppression d'image"
            >
                Vous êtes en train de supprimer cette image, voulez-vous continuer ?
            </EUIDialog>

            <Editor
                image={imageActive}
                isOpen={afficherModalModificationImage}
                onDismiss={this._toggleModal("ModificationImage", false, imageActive)}
                onDismissed={() => this.setState({ imageActive: {} })}
                onSave={this._onImageEdition}
            />

        </EUIPanel>;

    };

};